import {
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
    useSuspenseQuery,
} from '@tanstack/react-query';

import type { ApiFavoriteProduct } from '../api/userProfile/userProfileModels';

import userProfileFlow from '../flow/userProfileFlow';
import { ErrorGroup } from '../store/structureDefinitions/errorsState';
import { useGroupError } from './useGroupError';
import { useProfileMutations } from './useProfile';
import { useAppSelector } from './useThunkDispatch';
import {
    useIsShoppingForAnother,
    useIsUserAuthenticated,
    useShoppingUserId,
} from './useUserProfile';

const userFavoriteProductsQueryKeys = {
    all: ['UserFavoriteProducts'] as const,
    byUserId: (userId: string) => [...userFavoriteProductsQueryKeys.all, userId],
};

const getFavoriteProductsQueryOptions = (shoppingUserId: string) => {
    return queryOptions<ApiFavoriteProduct[]>({
        queryKey: userFavoriteProductsQueryKeys.byUserId(shoppingUserId),
        queryFn: () => userProfileFlow.favoriteProducts.getAll(),
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: false,
    });
};

export const useFavoriteProductsSuspenseQuery = () => {
    const shoppingUserId = useShoppingUserId() ?? 'anonymous';

    const query = useSuspenseQuery({
        ...getFavoriteProductsQueryOptions(shoppingUserId),
        refetchOnMount: 'always',
    });

    return query;
};

export const useFavoriteProductsQuery = () => {
    const isAuthenticated = useIsUserAuthenticated();
    const shoppingUserId = useShoppingUserId() ?? 'anonymous';

    const isEligibleForFavoriteProducts = useIsEligibleForFavoriteProducts();

    const query = useQuery<ApiFavoriteProduct[]>({
        ...getFavoriteProductsQueryOptions(shoppingUserId),
        enabled: isAuthenticated && !!shoppingUserId && isEligibleForFavoriteProducts,
    });

    return query;
};

export const useIsEligibleForFavoriteProducts = () => {
    const isShoppingForAnother = useIsShoppingForAnother();
    const isPunchout = useAppSelector((state) => state.user.punchout.isPunchout);

    return !isShoppingForAnother && !isPunchout;
};

export const useFavoriteProductsMutations = () => {
    const queryClient = useQueryClient();
    const shoppingUserId = useShoppingUserId() ?? 'anonymous';

    const { ensureProfileExists } = useProfileMutations();

    const optimisticAdd = (product: ApiFavoriteProduct) => {
        queryClient.setQueryData<ApiFavoriteProduct[]>(
            userFavoriteProductsQueryKeys.byUserId(shoppingUserId),
            (currentList) => {
                const list = currentList ?? [];
                if (list.some((p) => p.productId === product.productId)) return list;
                return [...list, product];
            },
        );
    };

    const optimisticRemove = (productId: string) => {
        let removedProduct: ApiFavoriteProduct | undefined;

        queryClient.setQueryData<ApiFavoriteProduct[]>(
            userFavoriteProductsQueryKeys.byUserId(shoppingUserId),
            (currentList) => {
                const list = currentList ?? [];
                removedProduct = list.find((p) => p.productId === productId);

                return list.filter((p) => p.productId !== productId);
            },
        );

        return removedProduct;
    };

    const addFavoriteProductMutation = useMutation({
        mutationFn: async (productId: string) => {
            await ensureProfileExists();
            return userProfileFlow.favoriteProducts.add(productId);
        },
        onMutate: async (productId) => {
            await queryClient.cancelQueries({
                queryKey: userFavoriteProductsQueryKeys.byUserId(shoppingUserId),
            });

            optimisticAdd({ productId, snapshotName: undefined, snapshotImageUrl: undefined });

            return { optimisticId: productId };
        },
        onSuccess: (addedProduct) => {
            queryClient.setQueryData<ApiFavoriteProduct[]>(
                userFavoriteProductsQueryKeys.byUserId(shoppingUserId),
                (listWithOptimisticProduct) => {
                    const list = listWithOptimisticProduct ?? [];
                    // filter out optimistic product id and add new full product from server;
                    const filtered = list.filter((p) => p.productId !== addedProduct.productId);
                    return [...filtered, addedProduct];
                },
            );
        },
        onError: (_error, _variables, context) => {
            if (context) {
                optimisticRemove(context.optimisticId);
            }
        },
    });

    const removeFavoriteProductMutation = useMutation({
        mutationFn: async (productId: string) => {
            await ensureProfileExists();
            return userProfileFlow.favoriteProducts.remove(productId);
        },
        onMutate: async (productId) => {
            await queryClient.cancelQueries({
                queryKey: userFavoriteProductsQueryKeys.byUserId(shoppingUserId),
            });

            const removedProduct = optimisticRemove(productId);

            return { removedProduct };
        },
        onError: (_error, _variables, context) => {
            if (context?.removedProduct) {
                optimisticAdd(context.removedProduct);
            }
        },
    });

    useGroupError(ErrorGroup.Global, removeFavoriteProductMutation.error);
    useGroupError(
        ErrorGroup.Global,
        addFavoriteProductMutation.error,
        undefined,
        (error) => error.code !== 'MAX_PRODUCT_LIMIT_REACHED',
    );

    return {
        addFavoriteProductMutation,
        removeFavoriteProductMutation,
    };
};
